/**
 * THE PURE HALF of Manage members (task #31) - Firestore-free so the overlay
 * composition is provable as plain data, the same split
 * lib/jrStaffBoardMembership.mjs's own "pure half" comment describes for the
 * derived-roll side. Kept in its own file, deliberately apart from
 * lib/boardMembership.ts's Firestore reads/writes: that file imports
 * ./firebase, which touches import.meta.env at module load and cannot be
 * imported by a plain node --test file. Nothing here ever will.
 */

import type { BoardKey } from './boardNotifications.ts';

export type { BoardKey };

/**
 * EVERY SURFACE THAT USES THIS OVERLAY SHAPE, not just the three boards
 * (Bobbie, 2026-09-01, verbatim: "dont change command center now. add a
 * button at the top that directors can add specific people to the command
 * center similar to the boards"). The Command Center is not a board and
 * never becomes one: it has no roll, no threads and no notification level,
 * so it is deliberately NOT added to BoardKey in ./boardNotifications. That
 * union drives the three-level notification setting, and a phantom fourth
 * board there would grow a preference nobody can act on. Widening THIS key
 * instead is what lets the Command Center reuse the overlay doc shape, the
 * pure composition helpers below, and the Firestore reads/writes in
 * ./boardMembership byte-for-byte, with no second copy of any of it.
 */
export type MembershipOverlayKey =
  // The comp parents board is a BoardKey (it has a notification level) but
  // never an overlay key: it has no client-readable overlay document at all.
  // Its membership is DERIVED server-side from the comp team roll plus its
  // own exceptions overlay at compParentsBoardMembershipOverlay/current
  // (DECISIONS entry 25), a collection with NO entry in
  // shared/firestore.rules, reachable only through
  // netlify/functions/compParentsBoard.mjs - so every function in this file
  // and in ./boardMembership would fail permission-denied on it.
  // Excluding it here makes that a compile error instead of a runtime one
  // (packet R43, ruling 9b).
  | Exclude<BoardKey, 'compParentsBoard'>
  | 'commandCenter';

export const OVERLAY_COLLECTION: Record<MembershipOverlayKey, string> = {
  compBoard: 'compBoardMembershipOverlay',
  jrStaffBoard: 'jrStaffBoardMembershipOverlay',
  parentBoard: 'parentBoardMembershipOverlay',
  commandCenter: 'commandCenterMembershipOverlay',
};

/**
 * The two derived-roll boards; the parent board has no roll doc of its own,
 * and neither does the Command Center (its only auto-in is the Director
 * role itself). watchBoardRollUids() no-ops for a keyless entry, so nothing
 * needs a special case.
 */
export const ROLL_COLLECTION: Partial<Record<MembershipOverlayKey, string>> = {
  compBoard: 'compBoardMembership',
  jrStaffBoard: 'jrStaffBoardMembership',
};

export interface BoardMembershipOverlay {
  addedUids: string[];
  removedUids: string[];
}

export const EMPTY_OVERLAY: BoardMembershipOverlay = { addedUids: [], removedUids: [] };

export function record(data: unknown): Record<string, unknown> {
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
}

export function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/** Add uid: a fresh add un-removes it too, so a manager never has to clear a stale removal by hand. */
export function withAddedMember(overlay: BoardMembershipOverlay, uid: string): BoardMembershipOverlay {
  return {
    addedUids: [...new Set([...overlay.addedUids, uid])],
    removedUids: overlay.removedUids.filter((id) => id !== uid),
  };
}

/** Remove uid: takes ACCESS (the rules' AND NOT removed), and drops any stale add for the same uid. */
export function withRemovedMember(overlay: BoardMembershipOverlay, uid: string): BoardMembershipOverlay {
  return {
    addedUids: overlay.addedUids.filter((id) => id !== uid),
    removedUids: [...new Set([...overlay.removedUids, uid])],
  };
}

/** Undo either override: back to whatever the derived roll/role says on its own. */
export function withClearedOverride(overlay: BoardMembershipOverlay, uid: string): BoardMembershipOverlay {
  return {
    addedUids: overlay.addedUids.filter((id) => id !== uid),
    removedUids: overlay.removedUids.filter((id) => id !== uid),
  };
}

/**
 * WHICH LOOKUP AN OVERLAY ANSWER BELONGS TO (Codex review on #835, P2).
 *
 * `settled` used to be a bare boolean, and it lied across a uid change. The
 * sequence that broke: the Command Center mounts before the user doc
 * resolves, so the hook is called with NO uid, the effect immediately sets
 * settled = true (there is nothing to wait for), and the page renders. Then
 * the user doc arrives, the caller decides this account IS eligible and
 * passes a real uid - but the effect that would set settled back to false
 * runs AFTER that render. For one frame the page therefore had
 * settled = true with an empty overlay and a real uid, which reads as a
 * confident "not a member" and drew the "Command Center is Kim's" denial
 * card at an invited Manager before the listener had even opened.
 *
 * The fix is to stop storing "did something settle" and start storing WHAT
 * settled. An answer carries the lookupKey it belongs to; the hook compares
 * that against the lookupKey it wants RIGHT NOW, during render, with no
 * effect in between. A uid change makes the old answer stale synchronously,
 * so the page falls to its loading branch rather than to a false denial.
 *
 * An absent uid has the empty-string key on purpose: there is genuinely
 * nothing to wait for, so NO_OVERLAY_ANSWER already answers it and the
 * caller settles on the first render instead of one render later.
 */
export interface OverlayAnswer {
  /** The lookup this overlay is the answer TO, from overlayLookupKey(). */
  lookupKey: string;
  overlay: BoardMembershipOverlay;
}

export const NO_OVERLAY_ANSWER: OverlayAnswer = { lookupKey: '', overlay: EMPTY_OVERLAY };

export function overlayLookupKey(
  key: MembershipOverlayKey,
  uid: string | null | undefined
): string {
  return uid ? `${key}:${uid}` : '';
}

/**
 * The overlay to trust for `lookupKey`, or null when the answer in hand
 * belongs to a DIFFERENT lookup and the caller must keep waiting. Null is
 * the "still loading" signal; never fall back to the stale overlay, because
 * one uid's membership is not evidence about another's.
 */
export function settledOverlayFor(
  answer: OverlayAnswer,
  lookupKey: string
): BoardMembershipOverlay | null {
  return answer.lookupKey === lookupKey ? answer.overlay : null;
}

/**
 * THE ONE SHARED DERIVATION every board page uses (Codex round 1 on #805,
 * finding 1). Byte-for-byte the same composition
 * shared/firestore.rules' isCompBoardMember() / isJrStaffBoardMember() /
 * isParentBoardMember() apply: effective access = (derived OR added) AND
 * NOT removed. `autoIn` is whatever that board's OWN pre-existing
 * predicate already computes (staff-by-role or roll membership for comp/
 * Jr Staff, "every active adult minus a dancer login" for the parent
 * board) - this function only adds the overlay half, so there is exactly
 * ONE place that spells out the composition, never three page-local
 * copies that could drift from the rules or from each other.
 */
export function isEffectiveBoardMember(input: {
  uid: string;
  autoIn: boolean;
  overlay: BoardMembershipOverlay;
}): boolean {
  const added = input.overlay.addedUids.includes(input.uid);
  const removed = input.overlay.removedUids.includes(input.uid);
  return (input.autoIn || added) && !removed;
}

/**
 * FIRESTORE LISTENER ERROR CLASSIFICATION (Codex P2a, PR #807 round 17).
 * An onSnapshot error is TERMINAL - the listener is dead, and nothing
 * resubscribes it on its own (lib/readEpoch.ts's own doc comment carries
 * the app-wide version of this same fact, proven there by execution: "a
 * Firestore `onSnapshot` error is TERMINAL. The listener is dead, nothing
 * resubscribes"). A hook that PRESERVES its last confirmed answer
 * across an error (round 15 for boardMembership.ts's overlay hook, round
 * 16 for jrStaffBoard.ts's roll hook) therefore preserves it FOREVER once
 * the listener has died, unless something rebuilds a fresh subscription -
 * never a security hole (the rules independently re-check the SAME
 * predicate on the actual protected read; see each hook's own docstring),
 * but it silently breaks the removal-propagation promise every fence built
 * on these two hooks depends on: a door that stays open until a full page
 * remount, no matter how long ago the roll or overlay actually changed.
 *
 * Two error shapes get two different responses, both hooks alike:
 *
 * - permission-denied is not a blip, and no resubscribe can clear it.
 *   READ THE RULE BEFORE REASONING ABOUT THIS ONE (round 17's own
 *   takeover corrected an earlier draft of this very comment, which
 *   claimed these docs gate their read on the board-membership predicate
 *   itself; they do not). Both docs these two hooks read - the derived
 *   roll `<board>Membership/current` and the overlay
 *   `<board>MembershipOverlay/current` - carry the SAME rule in
 *   shared/firestore.rules: `allow read: if docId == 'current' &&
 *   isActiveUserByDoc()`. Any active user may read either one, on purpose,
 *   so the Manage members sheet can load them BEFORE anyone's membership
 *   is known (watchBoardRollUids' own docstring says exactly this). So a
 *   denial here proves the caller is not an active user at all - signed
 *   out mid-session, status flipped off 'active', or retiredFirebaseDancer
 *   (liveUserDoc) - never merely "not a member". That is a property of the
 *   account, not of the network: a fresh listener would loop the identical
 *   denial forever, so there is nothing to retry. Fails closed
 *   immediately, no retry, and this OVERRIDES even a previously confirmed
 *   answer - an account the rules will not treat as active must not keep a
 *   board door open, and every protected read behind that door
 *   (isJrStaffBoardMember() and friends, all of which also require an
 *   active user doc) would be denied anyway. The bounded cost of being
 *   wrong: an account reactivated mid-session keeps this stale,
 *   fail-closed answer until the hook's uid changes or the page reloads,
 *   the same place every other fail-closed default in these two hooks
 *   already lands.
 * - a transient code (a network hiccup, a momentary server condition) says
 *   nothing about membership either way. The confirmed answer (if any) is
 *   left completely untouched while a bounded number of automatic
 *   resubscribe attempts run with backoff - the same unsubscribe/
 *   resubscribe SHAPE lib/compBoard.ts's useCompBoardRosterNames `refetch`
 *   already uses (an attempt counter riding the effect's own dependency
 *   array forces a fresh subscription), just driven automatically here
 *   rather than by a person's tap, since neither hook has a visible "Try
 *   again" of its own to drive it. Once attempts are exhausted, THIS also
 *   fails closed, overriding whatever was being preserved - an
 *   indefinitely retried, indefinitely stale confirmed answer is its own
 *   kind of wrong, not meaningfully safer than admitting the read failed.
 * - anything else (an error with no recognizable code, or a code neither
 *   list names) is treated the same as permission-denied: fails closed
 *   immediately, no retry. An unrecognized failure shape is not evidence
 *   the situation is transient, so it does not earn the transient path's
 *   trust.
 *
 * FirestoreError.code (confirmed against the installed SDK's own
 * FirestoreErrorCode type, node_modules/@firebase/firestore/dist/lite/
 * firestore/src/util/error.d.ts) is one of: 'cancelled' | 'unknown' |
 * 'invalid-argument' | 'deadline-exceeded' | 'not-found' | 'already-
 * exists' | 'permission-denied' | 'resource-exhausted' | 'failed-
 * precondition' | 'aborted' | 'out-of-range' | 'unimplemented' |
 * 'internal' | 'unavailable' | 'data-loss' | 'unauthenticated'.
 */
export const FIRESTORE_PERMISSION_DENIED_CODE = 'permission-denied';

/** The codes worth an automatic retry: momentary server or network
 *  conditions, never a statement about the caller's own membership. */
export const FIRESTORE_TRANSIENT_LISTENER_ERROR_CODES: ReadonlySet<string> = new Set([
  'unavailable',
  'deadline-exceeded',
  'internal',
  'resource-exhausted',
  'cancelled',
]);

/** Reads `.code` off an onSnapshot error callback's argument without
 *  assuming it is a real FirestoreError instance - defensive against a
 *  test double or a non-Firestore Error reaching the same callback. */
export function firestoreListenerErrorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

export function isTransientFirestoreListenerErrorCode(code: string | undefined): boolean {
  return code !== undefined && FIRESTORE_TRANSIENT_LISTENER_ERROR_CODES.has(code);
}

/** Bounded: three automatic resubscribe attempts, then fail closed rather
 *  than trust an increasingly stale confirmed answer forever. */
export const LISTENER_RETRY_MAX_ATTEMPTS = 3;

/** Exponential backoff, base one second: 1s, 2s, 4s for attempts 1, 2, 3 -
 *  `attemptNumber` is 1-based (the Nth retry, not the Nth error). */
export function listenerRetryDelayMs(attemptNumber: number): number {
  return 1000 * 2 ** (attemptNumber - 1);
}
